import { useEffect, useId, useState } from 'react';
import type { RefObject } from 'react';
import { toast } from 'react-toastify';
import type { WorkbookInstance } from '@fortune-sheet/react';
import Modal from '../../_custom/Modal/Modal';
import Button from '../../_custom/Button/Button';
import Input from '../../_custom/Input/Input';
import { cellToRefText } from './formulaRefs';
import { goToRange, resolveGoToRef } from './sheetActions';
import modalOverDrawerStyles from '../../ConfirmDialog/modalOverDrawer.module.scss';
import styles from './GoToDialog.module.scss';

/** Bounds the open-edge focus retry — `_custom/Modal` only flips its
 *  `display` to visible one render AFTER `open` becomes true (its own
 *  `useEffect -> setShow`), so a single rAF can land before the input is
 *  focusable. 10 frames is a generous margin over that one-render gap. */
const FOCUS_RETRY_MAX_FRAMES = 10;

export interface GoToDialogProps {
    open: boolean;
    onClose: () => void;
    workbookRef: RefObject<WorkbookInstance>;
}

/**
 * Ctrl/Cmd+G and F5 open this. Reuses `parseGoToRef` (the same parser the
 * Name Box uses) — an invalid ref shows an inline error and keeps the dialog
 * open; a valid-but-beyond-the-sheet ref still navigates (clamped to the
 * last row/column) and reports the clamp via a toast once closed, rather
 * than silently inserting rows.
 */
const GoToDialog: React.FC<GoToDialogProps> = ({ open, onClose, workbookRef }) => {
    const [value, setValue] = useState('');
    const [error, setError] = useState<string | null>(null);
    // Input isn't wrapped in forwardRef, so autofocus goes through the DOM id
    // instead of a ref.
    const inputId = useId();

    useEffect(() => {
        if (!open) return undefined;
        setValue('');
        setError(null);

        // Autofocus on open — the dialog mounts closed and Modal only toggles
        // visibility, so the input already exists, but Modal's own `display`
        // switch lands a render AFTER `open` does (R-3): a single rAF can hit
        // the input while it's still `display:none`, where `focus()` is a
        // silent no-op. Retry across a few frames until it's actually focusable.
        let attempts = 0;
        const tryFocus = (): void => {
            attempts += 1;
            const el = document.getElementById(inputId);
            if (el?.isConnected && el.offsetParent !== null) {
                el.focus();
                if (document.activeElement === el) return;
            }
            if (attempts < FOCUS_RETRY_MAX_FRAMES) frame = requestAnimationFrame(tryFocus);
        };
        let frame = requestAnimationFrame(tryFocus);

        return () => cancelAnimationFrame(frame);
    }, [open, inputId]);

    const handleGo = (): void => {
        const wb = workbookRef.current;
        if (!wb) return;
        const result = resolveGoToRef(wb, value);
        if (!result.ok) {
            setError(result.error);
            return;
        }
        goToRange(wb, result.range, result.sheetId);
        onClose();
        if (result.clamped) {
            const ref = cellToRefText(result.range.row[1], result.range.column[1]);
            toast.info(`Beyond the last row/column — jumped to ${ref}.`);
        }
    };

    return (
        <Modal open={open} onClose={onClose} size="flexible" className={modalOverDrawerStyles.modalOverDrawer} withoutCloseButton>
            <div className={styles.Dialog}>
                <label className={styles.Label} htmlFor={inputId}>
                    Go to
                </label>
                <Input
                    id={inputId}
                    inputClassName={styles.Input}
                    value={value}
                    onChange={(newValue: string) => {
                        setValue(newValue);
                        setError(null);
                    }}
                    onEnter={handleGo}
                    isValid={!error}
                    errorMessage={error ?? ''}
                    placeholder="e.g. B200, A1:C3, Sheet2!D4"
                    showFloatingLabel={false}
                    allowAllCharacters
                />
                <div className={modalOverDrawerStyles.actions}>
                    <Button text="Cancel" variant="purpleinverted" onClick={onClose} className={modalOverDrawerStyles.actionBtn} />
                    <Button
                        text="Go"
                        variant="primary"
                        onClick={handleGo}
                        disabled={!value.trim()}
                        className={modalOverDrawerStyles.actionBtn}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default GoToDialog;
